/**
 * Toddler Activity Renderers
 *
 * Each renderer takes a container element and creates an interactive
 * experience appropriate for a 1-3 year old on a landscape Fire 7 tablet.
 *
 * Design rules:
 * - Touch targets ≥ 80px
 * - No text the child needs to read (parent reads aloud)
 * - Warm, soft colors (Waldorf watercolor palette)
 * - Gentle sounds via Web Audio API (sine tones, not samples)
 * - Activities self-contained, 1-3 minute duration
 * - Repetition is welcome, not avoided
 */

import type { ToddlerActivity } from '../content/toddler-activities';
import {
  MORNING_VERSES,
  RHYTHM_PATTERNS,
  GARDEN_COLORS,
} from '../content/toddler-activities';

// ---------------------------------------------------------------------------
// Web Audio helpers — gentle pentatonic tones (Steiner favored pentatonic)
// ---------------------------------------------------------------------------

let audioCtx: AudioContext | null = null;

function getAudioCtx(): AudioContext {
  if (!audioCtx) audioCtx = new AudioContext();
  if (audioCtx.state === 'suspended') audioCtx.resume();
  return audioCtx;
}

/** Play a gentle pentatonic tone. */
function playTone(frequency: number, durationMs = 300, volume = 0.15): void {
  try {
    const ctx = getAudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = frequency;
    gain.gain.setValueAtTime(volume, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + durationMs / 1000);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + durationMs / 1000);
  } catch {
    // Audio not available — fine, visual feedback is enough
  }
}

// D pentatonic scale (Steiner/Waldorf instruments are tuned pentatonic)
const PENTATONIC = [293.66, 329.63, 369.99, 440.0, 493.88, 587.33, 659.25];

function randomPentatonic(): number {
  return PENTATONIC[Math.floor(Math.random() * PENTATONIC.length)];
}

// ---------------------------------------------------------------------------
// Activity renderers
// ---------------------------------------------------------------------------

export type CleanupFn = () => void;

export function renderActivity(
  container: HTMLElement,
  activity: ToddlerActivity,
): CleanupFn {
  container.innerHTML = '';

  switch (activity.type) {
    case 'rhythm-circle':
      return renderRhythmCircle(container, activity);
    case 'color-garden':
      return renderColorGarden(container, activity);
    case 'morning-verse':
      return renderMorningVerse(container, activity);
    case 'counting-stones':
      return renderCountingStones(container, activity);
    case 'breathing-stone':
      return renderBreathingStone(container, activity);
    default:
      container.innerHTML = '<p class="toddler-fallback">Activity coming soon</p>';
      return () => {};
  }
}

// ---------------------------------------------------------------------------
// 1. Rhythm Circle — pulsing dots in a ring, child taps in time
// ---------------------------------------------------------------------------

function renderRhythmCircle(container: HTMLElement, _activity: ToddlerActivity): CleanupFn {
  const pattern = RHYTHM_PATTERNS[0]; // waltz
  const beatCount = pattern.beats;

  container.innerHTML = `
    <div class="rhythm-circle">
      <div class="rhythm-circle__ring">
        ${Array.from({ length: beatCount }, (_, i) => `
          <div class="rhythm-circle__dot" data-beat="${i}">
            <div class="rhythm-circle__dot-inner"></div>
          </div>
        `).join('')}
      </div>
      <div class="rhythm-circle__feedback"></div>
      <p class="toddler-parent-note">Clap together with the pulsing light</p>
    </div>
  `;

  const dots = container.querySelectorAll<HTMLElement>('.rhythm-circle__dot');
  const feedback = container.querySelector<HTMLElement>('.rhythm-circle__feedback')!;
  let currentBeat = 0;
  let running = true;

  // Pulse loop
  const pulseBeat = () => {
    if (!running) return;

    dots.forEach(d => d.classList.remove('rhythm-circle__dot--active'));
    const activeDot = dots[currentBeat];
    if (activeDot) {
      activeDot.classList.add('rhythm-circle__dot--active');
      // Accent on beat 1
      const freq = currentBeat === 0 ? PENTATONIC[5] : PENTATONIC[2];
      playTone(freq, 200, currentBeat === 0 ? 0.12 : 0.06);
    }

    currentBeat = (currentBeat + 1) % beatCount;
    setTimeout(pulseBeat, pattern.loopMs / beatCount);
  };

  setTimeout(pulseBeat, 500);

  // Tap feedback
  const handleTap = (e: Event) => {
    e.preventDefault();
    feedback.classList.remove('rhythm-circle__feedback--flash');
    void feedback.offsetWidth; // force reflow
    feedback.classList.add('rhythm-circle__feedback--flash');
    playTone(randomPentatonic(), 150, 0.1);
  };

  container.addEventListener('touchstart', handleTap, { passive: false });
  container.addEventListener('click', handleTap);

  return () => {
    running = false;
    container.removeEventListener('touchstart', handleTap);
    container.removeEventListener('click', handleTap);
  };
}

// ---------------------------------------------------------------------------
// 2. Color Garden — touch to bloom watercolor circles
// ---------------------------------------------------------------------------

function renderColorGarden(container: HTMLElement, _activity: ToddlerActivity): CleanupFn {
  const canvas = document.createElement('canvas');
  canvas.className = 'color-garden__canvas';
  container.innerHTML = '<p class="toddler-parent-note">Touch anywhere to grow colors</p>';
  container.prepend(canvas);

  const resize = () => {
    canvas.width = container.clientWidth;
    canvas.height = container.clientHeight - 40; // leave space for note
  };
  resize();

  const ctx = canvas.getContext('2d')!;
  let blooms: { x: number; y: number; r: number; maxR: number; color: string; alpha: number }[] = [];
  let animId = 0;

  const addBloom = (x: number, y: number) => {
    const color = GARDEN_COLORS[Math.floor(Math.random() * GARDEN_COLORS.length)];
    const maxR = 40 + Math.random() * 60;
    blooms.push({ x, y, r: 5, maxR, color, alpha: 0.6 });
    playTone(randomPentatonic(), 400, 0.08);
  };

  const animate = () => {
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    blooms = blooms.filter(b => b.alpha > 0.02);

    for (const b of blooms) {
      if (b.r < b.maxR) b.r += (b.maxR - b.r) * 0.06;
      b.alpha *= 0.997;

      ctx.beginPath();
      ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2);
      ctx.fillStyle = b.color + Math.round(b.alpha * 255).toString(16).padStart(2, '0');
      ctx.fill();

      // Soft inner glow
      ctx.beginPath();
      ctx.arc(b.x, b.y, b.r * 0.5, 0, Math.PI * 2);
      ctx.fillStyle = b.color + Math.round(b.alpha * 0.5 * 255).toString(16).padStart(2, '0');
      ctx.fill();
    }

    animId = requestAnimationFrame(animate);
  };

  animate();

  const getPos = (e: TouchEvent | MouseEvent): { x: number; y: number } => {
    if ('touches' in e && e.touches.length > 0) {
      const rect = canvas.getBoundingClientRect();
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    if ('offsetX' in e) {
      return { x: (e as MouseEvent).offsetX, y: (e as MouseEvent).offsetY };
    }
    return { x: canvas.width / 2, y: canvas.height / 2 };
  };

  const handleTouch = (e: Event) => {
    e.preventDefault();
    const pos = getPos(e as TouchEvent | MouseEvent);
    addBloom(pos.x, pos.y);
  };

  const handleMove = (e: Event) => {
    e.preventDefault();
    const pos = getPos(e as TouchEvent | MouseEvent);
    // Only bloom on move every ~40px of distance
    if (blooms.length > 0) {
      const last = blooms[blooms.length - 1];
      const dist = Math.hypot(pos.x - last.x, pos.y - last.y);
      if (dist < 40) return;
    }
    addBloom(pos.x, pos.y);
  };

  canvas.addEventListener('touchstart', handleTouch, { passive: false });
  canvas.addEventListener('touchmove', handleMove, { passive: false });
  canvas.addEventListener('mousedown', handleTouch);
  canvas.addEventListener('mousemove', (e) => {
    if (e.buttons > 0) handleMove(e);
  });

  const resizeObs = new ResizeObserver(resize);
  resizeObs.observe(container);

  return () => {
    cancelAnimationFrame(animId);
    canvas.removeEventListener('touchstart', handleTouch);
    canvas.removeEventListener('touchmove', handleMove);
    canvas.removeEventListener('mousedown', handleTouch);
    resizeObs.disconnect();
  };
}

// ---------------------------------------------------------------------------
// 3. Morning Verse — tap to reveal lines, parent reads aloud
// ---------------------------------------------------------------------------

function renderMorningVerse(container: HTMLElement, _activity: ToddlerActivity): CleanupFn {
  const verse = MORNING_VERSES[Math.floor(Math.random() * MORNING_VERSES.length)];
  let currentLine = 0;

  container.innerHTML = `
    <div class="morning-verse">
      <div class="morning-verse__title">${verse.title}</div>
      <div class="morning-verse__lines"></div>
      <div class="morning-verse__attribution" style="display:none">${verse.attribution}</div>
      <div class="morning-verse__tap-hint">Touch to reveal each line</div>
      <p class="toddler-parent-note">Read each line aloud slowly. Touch to reveal the next.</p>
    </div>
  `;

  const linesEl = container.querySelector<HTMLElement>('.morning-verse__lines')!;
  const hintEl = container.querySelector<HTMLElement>('.morning-verse__tap-hint')!;
  const attrEl = container.querySelector<HTMLElement>('.morning-verse__attribution')!;

  const revealLine = () => {
    if (currentLine >= verse.lines.length) return;

    const lineEl = document.createElement('div');
    lineEl.className = 'morning-verse__line morning-verse__line--entering';
    lineEl.textContent = verse.lines[currentLine];
    linesEl.appendChild(lineEl);

    // Gentle ascending tone for each line
    const freq = PENTATONIC[currentLine % PENTATONIC.length];
    playTone(freq, 500, 0.06);

    currentLine++;

    if (currentLine >= verse.lines.length) {
      hintEl.style.display = 'none';
      attrEl.style.display = 'block';
    }

    requestAnimationFrame(() => lineEl.classList.remove('morning-verse__line--entering'));
  };

  const handleTap = (e: Event) => {
    e.preventDefault();
    revealLine();
  };

  container.addEventListener('touchstart', handleTap, { passive: false });
  container.addEventListener('click', handleTap);

  return () => {
    container.removeEventListener('touchstart', handleTap);
    container.removeEventListener('click', handleTap);
  };
}

// ---------------------------------------------------------------------------
// 4. Counting Stones — five stones to touch and count
// ---------------------------------------------------------------------------

function renderCountingStones(container: HTMLElement, _activity: ToddlerActivity): CleanupFn {
  const stoneCount = 5;
  const counted: boolean[] = new Array(stoneCount).fill(false);

  container.innerHTML = `
    <div class="counting-stones">
      <div class="counting-stones__field">
        ${Array.from({ length: stoneCount }, (_, i) => `
          <button class="counting-stones__stone" data-index="${i}" aria-label="Stone ${i + 1}">
            <span class="counting-stones__number"></span>
          </button>
        `).join('')}
      </div>
      <div class="counting-stones__total"></div>
      <p class="toddler-parent-note">Touch each stone and count aloud together</p>
    </div>
  `;

  const stones = container.querySelectorAll<HTMLElement>('.counting-stones__stone');
  const totalEl = container.querySelector<HTMLElement>('.counting-stones__total')!;
  let countSoFar = 0;

  stones.forEach((stone, i) => {
    const handler = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      if (counted[i]) return;

      counted[i] = true;
      countSoFar++;
      stone.classList.add('counting-stones__stone--counted');
      const numEl = stone.querySelector('.counting-stones__number')!;
      numEl.textContent = String(countSoFar);

      // Ascending tone
      playTone(PENTATONIC[countSoFar - 1] || PENTATONIC[0], 400, 0.1);

      if (countSoFar === stoneCount) {
        totalEl.textContent = 'Five!';
        totalEl.classList.add('counting-stones__total--complete');
        // Celebratory chord
        setTimeout(() => playTone(PENTATONIC[0], 600, 0.08), 100);
        setTimeout(() => playTone(PENTATONIC[2], 600, 0.08), 200);
        setTimeout(() => playTone(PENTATONIC[4], 600, 0.08), 300);
      }
    };

    stone.addEventListener('touchstart', handler, { passive: false });
    stone.addEventListener('click', handler);
  });

  return () => {};
}

// ---------------------------------------------------------------------------
// 5. Breathing Stone — a big gentle circle that breathes
// ---------------------------------------------------------------------------

function renderBreathingStone(container: HTMLElement, _activity: ToddlerActivity): CleanupFn {
  container.innerHTML = `
    <div class="breathing-stone">
      <div class="breathing-stone__circle"></div>
      <div class="breathing-stone__label">Breathe together</div>
      <div class="breathing-stone__phase">in...</div>
      <p class="toddler-parent-note">Breathe in as it grows, out as it shrinks</p>
    </div>
  `;

  const phaseEl = container.querySelector<HTMLElement>('.breathing-stone__phase')!;
  let running = true;
  let inPhase = true;

  const cycle = () => {
    if (!running) return;
    inPhase = !inPhase;
    phaseEl.textContent = inPhase ? 'in...' : 'out...';
    setTimeout(cycle, 4000); // 4s in, 4s out = 8s full cycle ≈ 7.5 breaths/min
  };

  setTimeout(cycle, 4000);

  return () => {
    running = false;
  };
}
